import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import style from "./ExpeditorActionsTab.module.scss";
import { Aviation } from "../../../../../../../services";
import { ORDER_STATUSES_DATA } from "../../../../../../../mock";
import CustomInput from "../../../../../../custom/custom-input/CustomInput";
import CustomButton from "../../../../../../custom/custom-button/CustomButton";
import ConfirmActionModal from "../../../../../../orders-new/order-info-expeditor/components/ConfirmActionModal/ConfirmActionModal";

const NEXT_STATUS = {
  accepted: "confirmed",
  confirmed: "onthetrip",
  onthetrip: "delivered",
};

const DEFAULT_ACTION_COPY = {
  title: "orderInfo.orderDecisionAviation.advanceOrderToNextStage",
  button: "orderInfo.orderDecisionAviation.advanceOrder",
  modalTitle: "orderInfo.orderDecisionAviation.confirmStatusChange",
  modalMessage: "orderInfo.orderDecisionAviation.areYouSureToProceed",
};

const CONFIRMED_ACTION_COPY = {
  title: "orderInfo.orderDecisionAviation.confirmLoadingTitle",
  button: "orderInfo.orderDecisionAviation.startFlight",
  modalTitle: "orderInfo.orderDecisionAviation.confirmLoadingCompletionTitle",
  modalMessage: "orderInfo.orderDecisionAviation.statusChangeParticipantsMessage",
};

const ON_THE_TRIP_ACTION_COPY = {
  title: "orderInfo.orderDecisionAviation.confirmArrivalTitle",
  button: "orderInfo.orderDecisionAviation.completeTransportation",
  modalTitle: "orderInfo.orderDecisionAviation.confirmTransportationCompletionTitle",
  modalMessage: "orderInfo.orderDecisionAviation.statusChangeParticipantsMessage",
};

const ExpeditorStatusAction = ({ order, fetchOrderData, onForceUpdate }) => {
  const { t } = useTranslation();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const [dimensionalWeight, setDimensionalWeight] = useState("");
  const [dimensionalWeightError, setDimensionalWeightError] = useState("");
  const [isWeightLoading, setIsWeightLoading] = useState(false);
  const status = String(order?.status || "").toLowerCase();
  const hasDimensionalWeight = Boolean(order?.dimensionalWeight);
  const actionCopy =
    status === "confirmed"
      ? CONFIRMED_ACTION_COPY
      : status === "onthetrip"
        ? ON_THE_TRIP_ACTION_COPY
        : DEFAULT_ACTION_COPY;
  const actionTitle =
    status === "onthetrip" && !hasDimensionalWeight
      ? "orderInfo.orderDecisionAviation.enterVolumetricWeightToProceed"
      : actionCopy.title;

  useEffect(() => {
    setDimensionalWeight(order?.dimensionalWeight ?? "");
    setDimensionalWeightError("");
    setIsConfirmOpen(false);
  }, [order?.dimensionalWeight, order?.id]);

  const refresh = async () => {
    await fetchOrderData?.();
    onForceUpdate?.();
  };

  const handleOpenConfirm = () => {
    if (status === "onthetrip" && !order?.dimensionalWeight) {
      toast.info(t("orderInfo.orderDecisionAviation.dimensionalWeightRequired"));
      setDimensionalWeightError(t("ordersCreate.validation.fillData"));
      return;
    }
    setIsConfirmOpen(true);
  };

  const handleConfirmStatus = async () => {
    const nextStatus = NEXT_STATUS[status];
    if (!order?.id || !nextStatus) return;

    try {
      setIsStatusLoading(true);
      await Aviation.updateStatus(order.id, nextStatus);
      const statusTitle = ORDER_STATUSES_DATA(t)[nextStatus]?.title || nextStatus;
      toast.success(
        t("orderInfo.orderDecisionAviation.statusUpdateSuccess", { statusTitle }),
      );
      setIsConfirmOpen(false);
      await refresh();
    } catch (error) {
      console.error(error);
      toast.error(t("orderInfo.genericError"));
    } finally {
      setIsStatusLoading(false);
    }
  };

  const handleSaveDimensionalWeight = async () => {
    const value = Number(dimensionalWeight);
    if (!value || value <= 0) {
      toast.info(t("orderInfo.orderDecisionAviation.enterDimensionalWeight"));
      setDimensionalWeightError(t("ordersCreate.validation.fillData"));
      return;
    }
    if (!order?.id) {
      toast.error(t("orderInfo.orderDecisionAviation.orderIdNotFound"));
      return;
    }

    try {
      setIsWeightLoading(true);
      await Aviation.updateDimensionalWeight(order.id, value);
      toast.success(t("orderInfo.orderDecisionAviation.dimensionalWeightSaved"));
      setDimensionalWeightError("");
      await refresh();
    } catch (error) {
      console.error(error);
      toast.error(t("orderInfo.genericError"));
    } finally {
      setIsWeightLoading(false);
    }
  };

  return (
    <div className={style.card}>
      <div className={style.topbar}>
        <span className={style.topbar_title}>{t(actionTitle)}</span>
      </div>

      <div className={style.actions_row_full}>
        <CustomButton
          text={t(actionCopy.button)}
          isNavy
          onClick={handleOpenConfirm}
          isLoading={isStatusLoading}
          disabled={isStatusLoading}
          style={{ width: "100%" }}
        />
      </div>

      {status === "onthetrip" ? (
        <div className={style.weight_row}>
          <div className={style.weight_input}>
            <CustomInput
              labelText={t("orderInfo.orderDecisionAviation.dimensionalWeightLabel")}
              placeholder={t("orderInfo.orderDecisionAviation.dimensionalWeightPlaceholder")}
              value={dimensionalWeight}
              onChangeValue={(value) => {
                setDimensionalWeight(value);
                setDimensionalWeightError("");
              }}
              isBgGray
              inputType="number"
              errorText={dimensionalWeightError}
            />
          </div>
          <div className={style.weight_button}>
            <CustomButton
              text={t("orderInfo.orderDecisionAviation.saveDimensionalWeight")}
              isNavy
              onClick={handleSaveDimensionalWeight}
              isLoading={isWeightLoading}
              disabled={isWeightLoading}
            />
          </div>
        </div>
      ) : null}

      <ConfirmActionModal
        isOpen={isConfirmOpen}
        title={t(actionCopy.modalTitle)}
        message={t(actionCopy.modalMessage)}
        onConfirm={handleConfirmStatus}
        onCancel={() => setIsConfirmOpen(false)}
        isLoading={isStatusLoading}
      />
    </div>
  );
};

export default ExpeditorStatusAction;

